import React from 'react';
import { Box, Typography, IconButton, CircularProgress, Alert, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { VerosimilitudEstado, VisitaEstado } from '../../consts/visitasStatus';
import { useTranslation } from 'react-i18next';

import {
    useGetVerosimilitudByIdQuery,
    usePatchVerosimilitudDataMutation,
    useDeleteVerosimilitudVisitMutation,
    useDownloadVisitaActaMutation
} from '../../../../services/visitasApi';

import { useAppDispatch } from '../../../../hooks/reduxHooks';
import { showAlert } from '../../../../features/slices/applicationSlice';
import { useSnackbar } from 'notistack';
import { skipToken } from '@reduxjs/toolkit/query';
import dayjs from 'dayjs';
import ActivitySummaryChip from './ActivitySummaryChip';
type OpenVerosimilitudParams = {
    mode: 'create' | 'edit';
    idVerosimilitud?: number;
};

interface VerosimilitudSectionProps {
    rgrlPresentaciones: any[];
    isVerosimilitudDraft: boolean;
    isVerosimilitudPendienteSinRespuestas: boolean;
    selectedVisita: any;
    shouldShowVerosimilitudRow: boolean;
    hasRgrlPresentations: boolean;
    loadingVerosimilitud?: boolean;
    loadingRgrl?: boolean;
    handleOpenVerosimilitud: (params: OpenVerosimilitudParams) => void;

    isFailedVisit: boolean;
    isReadOnly: boolean;
}

const VerosimilitudSection: React.FC<VerosimilitudSectionProps> = ({
    rgrlPresentaciones,
    isVerosimilitudDraft,
    selectedVisita,
    handleOpenVerosimilitud,
    hasRgrlPresentations,
    loadingVerosimilitud,
    isReadOnly
}) => {
    const { t } = useTranslation('visitas');
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [downloadVisitaActa] = useDownloadVisitaActaMutation();
    const [deleteVerosimilitudVisit] = useDeleteVerosimilitudVisitMutation();

    const verosimilitudActivity = selectedVisita?.actividades?.find(
        (a: any) => a.tipoVisita?.descripcion?.toUpperCase() === 'VEROSIMILITUD'
    );
    const visitState = selectedVisita?.estado;
    const canCreateOrEdit = [1, 8, 9].includes(visitState);
    const hasVerosimilitudEntity = Boolean(selectedVisita?.verosimilitud?.id);

    const getVerosimilitudEstado = (): VerosimilitudEstado => {
        if (hasVerosimilitudEntity) {
            return VerosimilitudEstado.PRESENTADO;
        }

        if (isVerosimilitudDraft) {
            return VerosimilitudEstado.BORRADOR;
        }

        if (verosimilitudActivity?.estado === 1) {
            return VerosimilitudEstado.PENDIENTE;
        }

        if (verosimilitudActivity?.estado === 2) {
            return VerosimilitudEstado.REALIZADA;
        }

        return VerosimilitudEstado.PENDIENTE;
    };

    const estadoVerosimilitud = getVerosimilitudEstado();

    const isPresentado = estadoVerosimilitud === VerosimilitudEstado.PRESENTADO;
    const isBorrador = estadoVerosimilitud === VerosimilitudEstado.BORRADOR;
    const isRealizada = estadoVerosimilitud === VerosimilitudEstado.REALIZADA;
    const isPendiente = estadoVerosimilitud === VerosimilitudEstado.PENDIENTE;

    const handleDownloadVerosimilitud = async () => {
        try {
            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadVerosimilitud.generating'),
                    loading: true
                })
            );

            const response = await downloadVisitaActa({
                idVisita: selectedVisita.id,
                idTipoVisita: selectedVisita.idTipoVisita,
                tipoActa: 2
            }).unwrap();

            if (!response?.data) {
                throw new Error('No se recibió el archivo');
            }

            const byteCharacters = atob(response.data);
            const byteNumbers = Array.from(byteCharacters, (c) => c.charCodeAt(0));
            const blob = new Blob([new Uint8Array(byteNumbers)], {
                type: 'application/pdf'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Verosimilitud_${selectedVisita.id}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);

            dispatch({ type: 'application/hideAlert' });
            enqueueSnackbar(t('alerts.downloadVerosimilitud.success'), {
                variant: 'success'
            });
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });

            const backendMessage =
                error?.data?.message || error?.error || t('alerts.downloadVerosimilitud.error');

            enqueueSnackbar(backendMessage, {
                variant: 'error'
            });
        }
    };

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            sx={{ borderBottom: '1px solid #ccc', padding: '12px 8px' }}
        >
            <Box display="flex" alignItems="center" gap={2}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase' }}>
                    {t('verosimilitud.title')}
                </Typography>

                {loadingVerosimilitud ? (
                    <CircularProgress size={24} />
                ) : (
                    <ActivitySummaryChip
                        status={isPresentado || isRealizada ? 'DONE' : 'PENDING'}
                    />
                )}
            </Box>

            <Box display="flex" gap={1}>
                {(isPresentado || isRealizada) && (
                    <Tooltip title={t('table.actions.downloadVerosimilitud')}>
                        <IconButton
                            onClick={handleDownloadVerosimilitud}
                            disabled={loadingVerosimilitud}
                        >
                            <FileDownloadIcon />
                        </IconButton>
                    </Tooltip>
                )}

                {isPresentado && (
                    <IconButton
                        disabled={isReadOnly}
                        onClick={() =>
                            handleOpenVerosimilitud({
                                mode: 'edit',
                                idVerosimilitud: selectedVisita.verosimilitud.id
                            })
                        }
                    >
                        <EditIcon />
                    </IconButton>
                )}

                {(isBorrador || isRealizada) && canCreateOrEdit && (
                    <IconButton
                        disabled={isReadOnly}
                        onClick={() =>
                            handleOpenVerosimilitud({
                                mode: 'edit',
                                idVerosimilitud: selectedVisita?.verosimilitud?.id
                            })
                        }
                    >
                        <EditIcon />
                    </IconButton>
                )}

                {isPendiente && hasRgrlPresentations && (
                    <IconButton
                        disabled={isReadOnly}
                        onClick={() =>
                            handleOpenVerosimilitud({
                                mode: 'create'
                            })
                        }
                    >
                        <EditIcon />
                    </IconButton>
                )}
            </Box>
        </Box>
    );
};

export default VerosimilitudSection;
